import Backbone from 'backbone'
import template from './dataset-field-selector.hbs'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import LoadingDots from 'scoreboard/components/loading-dots'
import Dataset from 'scoreboard/datasets/model/dataset'
import $ from 'jquery'
import DatasetFieldList from './dataset-field-list'

/**
 * A selector for dataset fields; meant to be used as a 2nd step after selecting
 * an organization and dataset
 */
const DatasetFieldSelector = Backbone.View.extend({

	className: 'dataset-field-selector',

	/**
	 *
	 * @param {Object} opt
	 *
	 * @param {String} [opt.noFieldsMessage='datasets.noFields'] message to be displayed if there are no
	 * valid fields to select. This can also be a function that accepts the dataset
	 * as the first parameter
	 *
	 * @param {Boolean} [opt.allowEditFields=false] whether to include the
	 * Edit pseudo-fields (i.e. Record is Editable, Record Edited)
	 */
	initialize: function (opt = {}) {
		this.fieldListFilter = opt.fieldListFilter || _.constant(true)
		this.allowTotal = opt.allowTotal
		this.allowAllTime = opt.allowAllTime
		this.allowDateField = opt.allowDateField
		this.allowEditFields = opt.allowEditFields
		this.noFieldsMessage = opt.noFieldsMessage || i18n.get('datasets.noFields')
		this.allowVisibleLinkedFields = opt.allowVisibleLinkedFields
		this.fieldListSort = opt.fieldListSort
	},

	render: function () {
		this.$el.html(template())

		this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container') })

		return this
	},

	/**
	 * Called before setDataset to clear out the existing data
	 * @private
	 */
	_beforeSetDataset: function() {
		this._removeFieldList()
		this.loadingDots.render()

		if (this.xhr) {
			this.xhr.abort()
			delete this.xhr
		}
	},

	/**
	 * Called after setDataset to remove the loading animation
	 * @private
	 */
	_afterSetDataset: function() {
		delete this.xhr
		this.loadingDots.clear()
	},

	/**
	 * Renders the fields for the given dataset ID. Note that this fetches the
	 * dataset from the server; if you already have the dataset model, use
	 * setDataset() instead to skip the fetch
	 *
	 * @param {Integer} datasetId
	 */
	setDatasetId: function (datasetId) {
		this._beforeSetDataset()

		let params = $.param({
			fields: true,
			linkedDatasets: true,
			permissions: true
		})

		this.trigger('fetch')

		this.xhr = $.get(`api/datasets/${datasetId}?${params}`, (data) => {
			this._afterSetDataset()

			// if user was able to pick the dataset, they were able to view it
			data.view = true

			var dataset = new Dataset(data)

			this.setDataset(dataset, { fromXHR: true })
		})
	},

	/**
	 * Renders the fields for the given dataset.
	 *
	 * @param {Dataset} dataset
	 */
	setDataset: function (dataset, opt = {}) {
		// These can be performed back to back as there is no server call to make
		this._beforeSetDataset()
		this._afterSetDataset()

		const noFieldsMessage = _.isFunction(this.noFieldsMessage)
			? this.noFieldsMessage(dataset) : this.noFieldsMessage
		this.fieldList = new DatasetFieldList({
			model: dataset,
			filter: this.fieldListFilter,
			allowTotal: this.allowTotal,
			allowAllTime: this.allowAllTime,
			allowDateField: this.allowDateField,
			allowEditFields: this.allowEditFields,
			noFieldsMessage,
			allowVisibleLinkedFields: this.allowVisibleLinkedFields,
			sort: this.fieldListSort
		})
		this.$el.append(this.fieldList.el)
		this.fieldList.render()
		this.listenTo(this.fieldList, 'select', selected => this.trigger('select', selected))

		this.trigger('ready', _.pick(opt, 'fromXHR'))
	},

	_removeFieldList: function () {
		this.fieldList && this.fieldList.remove()
		delete this.fieldList
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeFieldList()
	}
})

export default DatasetFieldSelector

DatasetFieldSelector.__filename__ = 'components/bootslide/dataset-field-selector.js'
